<script lang="ts">
  import type { Snippet } from 'svelte';
  import TopNav from './TopNav.svelte';
  import LegalFooter from './LegalFooter.svelte';
  import ToastHost from './ToastHost.svelte';
  import { fromQuery } from '../lib/stores/toast';

  // Shell for non-game routes. Pairs with AppLayout (game shell) — each
  // route picks one explicitly per the per-route ownership pattern in
  // App.svelte:14-19. Owns: top nav, centered main column, legal footer,
  // toast host, and the document.title side-effect.

  interface Props {
    /** Cap content at ~640px for forms / legal text. Default false (full-width). */
    narrow?: boolean;
    /** Sets document.title (suffixed with " · AtlasGuess"). Optional. */
    title?: string;
    children: Snippet;
  }

  let { narrow = false, title, children }: Props = $props();

  $effect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = `${title} · AtlasGuess`;
    return () => {
      document.title = previous;
    };
  });

  // On first mount, fire any toasts encoded in ?status / ?error and
  // strip them from the URL so a refresh doesn't re-fire. We only do
  // this once per mount — not per nav — because svelte-routing keeps
  // PageShell mounted across same-shell route changes.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('status') && !url.searchParams.has('error')) return;
    const cleaned = fromQuery(url);
    window.history.replaceState({}, '', cleaned.toString());
  });
</script>

<div class="page-shell">
  <TopNav />
  <main class="page-shell__main" class:page-shell__main--narrow={narrow}>
    {#if title}
      <h1 class="page-shell__title">{title}</h1>
    {/if}
    {@render children()}
  </main>
  <LegalFooter />
  <ToastHost />
</div>
